import { useCallback } from 'react'
import { Button, Typography, message as antMessage } from 'antd'
import { Trash2, PanelLeftClose, PanelLeft } from 'lucide-react'
import { useChatStore } from '@/store/chatStore'
import { useUIStore } from '@/store/uiStore'
import { useFileStore } from '@/store/fileStore'
import { useStreamQuery } from '@/hooks/useStreamQuery'
import { useDemoMode } from '@/hooks/useDemoMode'
import { MessageList } from '../MessageList'
import { ChatInput } from '../ChatInput'
import { DemoQueryButtons } from '../DemoQueryButtons'
import { DemoBlockedMessage } from '../DemoBlockedMessage'
import { t } from '@/config/translations'
import { branding } from '@/config/branding'
import type { QueryRequest } from '@/types/api.types'
import styles from './ChatContainer.module.css'

const { Title } = Typography

export function ChatContainer() {
  const { messages, sessionId, clearChat } = useChatStore()
  const {
    selectedProjectId,
    selectedScenario,
    activeTags,
    sidebarCollapsed,
    setSidebarCollapsed,
  } = useUIStore()
  const { uploadedFiles } = useFileStore()
  const { isDemoMode, queriesCount, isBlocked } = useDemoMode()

  const { sendQuery, isStreaming, abortStream } = useStreamQuery()

  const handleSend = useCallback(
    (query: string) => {
      if (isStreaming || isBlocked) return

      const request: QueryRequest = {
        query,
        scenario: selectedScenario,
        search_mode: 'smart', // Always use smart mode - auto-chooses between direct search and agent
        session_id: sessionId,
        project_id: isDemoMode ? 'demo' : selectedProjectId,
      }

      // Add attached_files only if present (exactOptionalPropertyTypes)
      if (uploadedFiles.length > 0) {
        request.attached_files = uploadedFiles
      }

      // Add active_tags only if tags are selected
      if (activeTags.length > 0) {
        request.active_tags = activeTags
      }

      void (async () => {
        try {
          await sendQuery(request)
        } catch (error) {
          console.error('Send query error:', error)
          void antMessage.error(t('connection_error', { error: 'Failed to send query' }))
        }
      })()
    },
    [selectedProjectId, selectedScenario, sessionId, uploadedFiles, activeTags, sendQuery, isDemoMode, isBlocked, isStreaming]
  )

  const handleClearChat = useCallback(() => {
    clearChat()
    void antMessage.success(t('chat_cleared'))
  }, [clearChat])

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed)
  }, [sidebarCollapsed, setSidebarCollapsed])

  // Render demo-specific UI based on state
  const renderContent = () => {
    // Demo mode: Show query buttons before any messages
    if (isDemoMode && queriesCount === 0) {
      return <DemoQueryButtons onSelectQuery={handleSend} disabled={isStreaming} />
    }

    // Normal mode or demo with queries remaining: Show chat
    return (
      <>
        {/* Messages */}
        <div className={styles['messagesContainer']}>
          <MessageList messages={messages} isStreaming={isStreaming} onSendMessage={isBlocked ? undefined : handleSend} />
        </div>

        {/* Input */}
        <div className={styles['inputContainer']}>
          {isBlocked && !isStreaming ? <DemoBlockedMessage /> : <ChatInput
            onSend={handleSend}
            onStop={abortStream}
            isStreaming={isStreaming}
            disabled={isBlocked && !isStreaming}
          />}
        </div>
      </>
    )
  }

  return (
    <div className={styles['chatContainer']}>
      {/* Header */}
      <div className={styles['header']}>
        <div className={styles['headerLeft']}>
          <Button
            icon={sidebarCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
            onClick={toggleSidebar}
            type="text"
            className={styles['toggleButton'] || undefined}
            aria-label={sidebarCollapsed ? 'Открыть панель' : 'Закрыть панель'}
          />
          <Title level={4} className={styles['title'] || undefined}>
            <span className={styles['icon']}>{branding.appIcon}</span>
            {branding.appName}
          </Title>
        </div>
        <div className={styles['headerRight']}>
          <Button
            icon={<Trash2 size={16} />}
            onClick={handleClearChat}
            disabled={isStreaming || messages.length === 0}
            danger
            type="text"
            className={styles['clearButton'] || undefined}
          >
            <span className={styles['clearButtonText']}>{t('clear_chat')}</span>
          </Button>
        </div>
      </div>

      {/* Dynamic Content */}
      {renderContent()}
    </div>
  )
}
